<template>
  <div>
    <b-container fluid class="tw-mb-5 tw-bg-gray-100 tw-border tw-border-blue-200" style="padding-bottom: 10px;">
      <b-row class="tw-mt-2">
        <b-col cols="auto">
          <b-form-select size="lg" v-model="category" :options="categoryOptions"></b-form-select>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="12" lg="10" :xl="9" class="tw-overflow-x-scroll" ref="clusterMappingChartContainer">
          <bar-chart
            v-if="loaded"
            :chartData="clusterMapping.chartdata"
            :options="clusterMapping.options"
            :styles="clusterMapping.myStyles"
            :key="componentKey"
          >
          </bar-chart>
          <span class="text-secondary tw-text-xs text-center" v-if="last_update">Last Updated: {{last_update}}</span>
        </b-col>
        <b-col md="12" lg="2" :xl="3">
          <b-container>
            <b-row>
              <b-col lg="12" :xl="10" class="tw-p-1">
                <b-card>
                  <b-card-text>
                    <h6 class="tw-font-bolder tw-mb-0 text-center">
                      Cluster Mapped
                    </h6>
                    <p class="text-secondary tw-text-xs text-center">
                      Cluster Targets Reached
                    </p>
                    <h1 class="tw-text-3xl tw-font-bolder text-center tw-mb-0">
                      {{ clusterReachedMappingTarget }}/30
                    </h1>
                  </b-card-text>
                </b-card>
              </b-col>
            </b-row>
            <b-row>
              <b-col lg="12" :xl="10" class="tw-p-1">
                <b-card>
                  <b-card-text>
                    <h6 class="tw-font-bolder tw-mb-0 text-center">
                      Subjects
                    </h6>
                    <p class="text-secondary tw-text-xs text-center">
                      <span v-if="category=='total_children'">Total Children</span>
                      <span v-if="category=='total_residents'">Total Residents</span>
                    </p>

                    <h1 class="tw-text-2xl tw-font-bolder text-center tw-mb-0">
                      {{ totalCluster }}
                    </h1>
                  </b-card-text>
                </b-card>
              </b-col>
            </b-row>
          </b-container>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <div class="tw-mt-auto tw-flex tw-justify-end">
            <info-popover>
              <template v-slot:title>
                Cluster Mapping
              </template>
              <p>For each cluster, show how many potential subjects are mapped, and which have met the targets.</p>
            </info-popover>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";

import * as _ from "lodash";

import moment from 'moment-timezone';

import BarChart from "@/components/charts/BarChart.vue";

export default {
  mixins: [],
  components: {
    BarChart,
  },
  props: {},
  data() {
    return {
      negomboColor: "#464D77",
      wattalaColor: "#36827F",
      kalaniyaColor: "#F9DB6D",
      last_update: null,
      loaded: false,
      componentKey: 0,  // Hacky way to make the chart Y-axis label refresh when it changes.
      category: 'total_children',
      categoryOptions: [
        {value: 'total_children', text: 'Subjects from 4-16'},
        {value: 'total_residents', text: 'All Household Members'}
      ],
      totalCluster: 0,
      mappingTarget: 140,
      clusterReachedMappingTarget: 0,
      clusterMapping: {
        chartdata: {
          datasets: [
            {
              label: "Cluster",
              backgroundColor: "red",
              data: [45, 23],
            },
          ],
          labels: [1, 2],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: "top",
            labels: {
              boxWidth: 18,
            },
            onClick: (e) => e.stopPropagation() // disable clicking on the legend to remove bars
          },
          title: {
            display: true,
            text: "Cluster Mapping Progress",
          },
          animation: {
            animateScale: true,
            animateRotate: true,
          },
          scales: {
            xAxes: [
              {
                stacked: false,
                gridLines: {
                  display: true,
                },
                scaleLabel: {
                  display: true,
                  labelString: "Cluster",
                },
              },
            ],
            yAxes: [
              {
                stacked: false,
                gridLines: {
                  display: true,
                },
                scaleLabel: {
                  display: true,
                  labelString: "Potential Subjects, Aged 4-16",
                },
                ticks: {
                  beginAtZero: true,
                },
              },
            ],
          },
        },
        myStyles: {
          height: "35vh",
          width: "150vh",
          position: "relative",
        },
      },
    };
  },
  computed: {},
  watch: {
    category: function(value) {
      this.get();
      if (value=="total_children"){
        this.clusterMapping.options.scales.yAxes[0].scaleLabel.labelString = 'Potential Subjects, Aged 4-16';
        this.mappingTarget = 140;
      }else if (value=="total_residents"){
        this.clusterMapping.options.scales.yAxes[0].scaleLabel.labelString = 'All Household Residents';
        this.mappingTarget = 660;
        
      }
      this.componentKey++;
    },
  },
  created: function() {
    HTTP.get("/lib/get_last_updated_at/update_cluster_mapping", {})
      .then((response) => {
        this.last_update = moment(response.data['last_update']).tz(moment.tz.guess());
      })
  },
  mounted: function() {
    this.get();
  },
  methods: {
    get: function() {
      let datasets = [];

      datasets = [
        { // This is the only "real" dataset that will show data on the bar chart
          label: "Negombo",
          // Conditionally color the bars by cluster number
          backgroundColor: color => {
            let colors = [];
            for (let x = 1; x <= 30; x++) {
              if (x < 11) {
                colors.push(this.negomboColor);
              } else if (x > 10 && x < 21){
                colors.push(this.wattalaColor);
              } else {
                colors.push(this.kalaniyaColor);
              }
            }
            return colors;
          },
          data: [],
        },
        { // This dataset is only here for the legend label
          label: "Wattala",
          type: "line", // If we set this as "Bar" it will compete with the real dataset for width
          backgroundColor: this.wattalaColor,
          borderColor: this.wattalaColor,
          data: [],
        },
        { // This dataset is only here for the legend label
          label: "Kelaniya",
          type: "line", // If we set this as "Bar" it will compete with the real dataset for width
          backgroundColor: this.kalaniyaColor,
          borderColor: this.kalaniyaColor,
          data: [],
        },
        {
          type: "line",
          backgroundColor: "red",
          borderColor: "red",
          fill: false,
          pointRadius: 0,
          borderWidth: 1,
          label: "Mapping Target",
          data: [],
        },
      ];

      let labels = [];
      let cluster = {};
      let cluster_number = '';

      const that = this;
      HTTP.get("/srilanka/metrics/cluster-mapping", {params: {category: that.category}})
        .then((response) => {
          that.totalCluster = 0;
          that.clusterReachedMappingTarget = 0;
          _.each(response.data, function(item) {
            cluster_number = item.cluster_id.split("r")[1];
            cluster[cluster_number] = item;
          });

          for (let x = 1; x <= 30; x++) {
            if (x in cluster) {
              datasets[0].data.push(cluster[x]['count']);
              that.totalCluster += cluster[x]['count'];

              if (cluster[x]['count'] >= that.mappingTarget) {
                that.clusterReachedMappingTarget += 1;
              }
            } else {
              datasets[0].data.push(0);
            }
            labels.push(x);

            datasets[3].data.push(that.mappingTarget);
          }

          that.clusterMapping.chartdata = {
              datasets: datasets,
              labels: labels
          };

          that.loaded = true;

          // setTimeout(function() {
          //   that.$refs['clusterMappingChartContainer'].scrollLeft = that.$refs['clusterMappingChartContainer'].scrollWidth / 3.5;
          // }, 500);
        })
        .catch((error) => {
          HandleErrors.HandleErrors(error);
        });
    },
    find: function() {},
    create: function() {},
    patch: function() {},
    update: function() {},
    delete: function() {},
  },
};
</script>

<style>

/* #bar-chart {
  height: 240px !important;
} */
</style>
